import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    Typography,
    Button,
    Box,
    Paper,
    TextField,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    IconButton,
    Tooltip,
    Snackbar,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import DownloadIcon from '@mui/icons-material/Download';
import BarChartIcon from '@mui/icons-material/BarChart';
import ShareIcon from '@mui/icons-material/Share';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import apiService from '../services/api';
import ChartRenderer from '../components/ChartRenderer';
import ChatSidebar from '../components/ChatSidebar';
import {
    extractChartData,
    exportChatToPdf
} from '../utils/pdfExport';

const Chat = ({ sessionId, setSessionId, messages, setMessages, selectedCountry, selectedYear }) => {
    const [currentMessage, setCurrentMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [initializing, setInitializing] = useState(false);
    const [error, setError] = useState('');
    const [copySnackbar, setCopySnackbar] = useState(false);
    const [exportingPdf, setExportingPdf] = useState(false);
    const [sidebarRefreshTrigger, setSidebarRefreshTrigger] = useState(0);
    const [isTemporarySession, setIsTemporarySession] = useState(false);
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [shareUrl, setShareUrl] = useState('');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const messagesEndRef = useRef(null);
    const chatContainerRef = useRef(null);

    // Get location state for report data and URL parameters
    const location = useLocation();
    const navigate = useNavigate();
    const { chatId } = useParams();
    const [initializedWithReport, setInitializedWithReport] = useState(false);

    // Initialize chat session only when coming from report generation
    useEffect(() => {
        // Check if we have report data from navigation state and haven't initialized with it yet
        if (location.state?.initWithReport && location.state?.reportData && !initializedWithReport) {
            // Initialize chat with report data
            initializeChat(location.state.reportData);
            setInitializedWithReport(true);
        }
        // Note: Removed automatic initialization - users must click "+" to create new chats
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state, initializedWithReport]);

    // Load specific chat from URL parameter
    useEffect(() => {
        if (chatId && !sessionId && !isTemporarySession && !initializing) {
            console.log('[CHAT DEBUG] Loading chat from URL:', chatId);
            loadChatSession(chatId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatId, sessionId, isTemporarySession, initializing]);

    // Track previous country to detect changes
    const [previousCountry, setPreviousCountry] = useState(selectedCountry);

    // Handle country changes
    useEffect(() => {
        // Only reinitialize if the country has changed and it's not the initial render
        if (selectedCountry && previousCountry && selectedCountry !== previousCountry) {
            // Update the session with the new country without wiping messages
            updateChatCountry();
        }

        // Update previous country
        setPreviousCountry(selectedCountry);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCountry]);

    // Update chat session for new country without wiping messages
    const updateChatCountry = async () => {
        try {
            setError('');

            // If we have a session, just add an informational message about country change
            if (sessionId) {
                setMessages(prev => [
                    ...prev,
                    {
                        role: 'assistant',
                        content: `Country context switched to ${selectedCountry}. I'll now provide insights based on ${selectedCountry} data.`,
                        timestamp: new Date().toISOString()
                    }
                ]);
            } else {
                // If no session exists yet, initialize one
                initializeChat();
            }
        } catch (err) {
            console.error('Error updating chat country:', err);
            setError('Failed to update country context. Please try again.');
        }
    };

    // Auto-scroll to bottom when new messages are added
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const initializeChat = async (reportData = null) => {
        try {
            setInitializing(true);
            setError('');

            const response = await apiService.initializeChat(reportData, selectedCountry, selectedYear);

            if (response.data.session_id) {
                setSessionId(response.data.session_id);
                // Load the initial messages from the backend (including the welcome message)
                await loadChatSession(response.data.session_id, true);
            } else {
                setError('Failed to initialize chat session');
            }
        } catch (err) {
            console.error('Error initializing chat:', err);
            setError('Failed to initialize chat session. Please try again.');
        } finally {
            setInitializing(false);
        }
    };

    const sendMessage = async () => {
        if (!currentMessage.trim() || !sessionId || loading) {
            return;
        }

        const userMessage = currentMessage.trim();
        setCurrentMessage('');
        setLoading(true);
        setError('');

        // Log the message being sent
        console.log('[CHAT DEBUG] Sending message to backend:', userMessage);
        console.log('[CHAT DEBUG] Selected country:', selectedCountry);
        console.log('[CHAT DEBUG] Is temporary session:', isTemporarySession);

        // Add user message to the chat
        const newUserMessage = {
            role: 'user',
            content: userMessage,
            timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, newUserMessage]);

        try {
            let actualSessionId = sessionId;

            // If this is a temporary session, create the real session first
            if (isTemporarySession) {
                console.log('[CHAT DEBUG] Creating real session for first message');
                const initResponse = await apiService.initializeChat(null, selectedCountry, selectedYear);
                
                if (initResponse.data.session_id) {
                    actualSessionId = initResponse.data.session_id;
                    setSessionId(actualSessionId);
                    setIsTemporarySession(false);
                    
                    // Update URL to include the chat ID
                    navigate(`/chat/${actualSessionId}`, { replace: true });
                    
                    console.log('[CHAT DEBUG] Created real session:', actualSessionId);
                } else {
                    throw new Error('Failed to create chat session');
                }
            }

            const response = await apiService.sendChatMessage(actualSessionId, userMessage, selectedCountry);

            // Log the response received
            console.log('[CHAT DEBUG] Received response from backend:', response.data);

            if (response.data.response) {
                // Add assistant response to the chat
                const assistantMessage = {
                    role: 'assistant',
                    content: response.data.response,
                    timestamp: new Date().toISOString(),
                    toolCalls: response.data.tool_calls || null
                };
                setMessages(prev => [...prev, assistantMessage]);
                
                // Refresh sidebar after exchange to show the new session or updated title
                setSidebarRefreshTrigger(prev => prev + 1);
            } else {
                setError('No response received from the assistant');
            }
        } catch (err) {
            console.error('[CHAT DEBUG] Error sending message:', err);
            const errorMessage = err.response?.data?.detail || 'Failed to send message. Please try again.';
            setError(errorMessage);

            // Add error message to chat
            const errorChatMessage = {
                role: 'assistant',
                content: `I'm sorry, I encountered an error: ${errorMessage}`,
                timestamp: new Date().toISOString(),
                isError: true
            };
            setMessages(prev => [...prev, errorChatMessage]);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setCopySnackbar(true);
    };

    const formatTimestamp = (timestamp) => {
        return new Date(timestamp).toLocaleTimeString();
    };

    const handleExportToPdf = async () => {
        await exportChatToPdf(messages, sessionId, selectedCountry, setExportingPdf, setError);
    };

    // Function to handle sharing the chat
    const handleShareChat = () => {
        if (sessionId && !isTemporarySession) {
            const currentUrl = window.location.origin;
            const shareLink = `${currentUrl}/share/chat/${sessionId}`;
            setShareUrl(shareLink);
            setShareDialogOpen(true);
        }
    };

    // Function to copy share URL to clipboard
    const handleCopyShareUrl = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopySnackbar(true);
            setShareDialogOpen(false);
        } catch (err) {
            console.error('Failed to copy URL:', err);
        }
    };

    const loadChatSession = async (selectedSessionId, skipLoadingState = false) => {
        try {
            if (!skipLoadingState) {
                setLoading(true);
            }
            setError('');

            // Fetch the session messages
            const response = await apiService.getChatSessionMessages(selectedSessionId);
            const sessionMessages = response.data.messages;

            // Set the session ID and messages
            setSessionId(selectedSessionId);
            setIsTemporarySession(false); // This is a real session from the database
            setMessages(sessionMessages.map(msg => ({
                role: msg.role,
                content: msg.content,
                timestamp: msg.timestamp
            })));

        } catch (err) {
            console.error('Error loading chat session:', err);
            
            // If this was loaded from URL and the session doesn't exist, redirect to base chat page
            if (chatId === selectedSessionId && err.response?.status === 404) {
                console.log('[CHAT DEBUG] Chat not found, redirecting to base chat page');
                navigate('/chat', { replace: true });
                setError('Chat session not found. You may have followed an invalid or expired link.');
            } else {
                setError('Failed to load chat session. Please try again.');
            }
        } finally {
            if (!skipLoadingState) {
                setLoading(false);
            }
        }
    };

    const handleSessionSelect = (selectedSessionId) => {
        if (selectedSessionId !== sessionId) {
            setIsTemporarySession(false); // Clear temporary session state
            loadChatSession(selectedSessionId);
            // Update URL to match the selected chat
            navigate(`/chat/${selectedSessionId}`, { replace: true });
        }
    };

    const handleNewChat = () => {
        // Prevent multiple clicks while initializing
        if (initializing) return;
        
        // Clear current session and messages
        setSessionId(null);
        setMessages([]);
        
        // Clear URL (navigate to base chat page)
        navigate('/chat', { replace: true });
        
        // Create a temporary session with welcome message (not persisted yet)
        const tempSessionId = `temp_${Date.now()}`;
        setSessionId(tempSessionId);
        setIsTemporarySession(true);
        
        // Add welcome message locally
        setMessages([{
            role: 'assistant',
            content: `Hello! I'm your data analysis assistant. I can help you explore and analyze the BrandSpark ® Trust Survey data. You can ask me questions about brands, categories, trust metrics, consumer insights, or request specific data queries. What would you like to know?`,
            timestamp: new Date().toISOString()
        }]);
    };

    if (initializing) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
                <Typography variant="body1" sx={{ ml: 2 }}>
                    Initializing chat session...
                </Typography>
            </Box>
        );
    }

    // Show empty state when no session is selected
    if (!sessionId && !initializing) {
        return (
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                height: '100%', 
                maxHeight: '100%',
                overflow: 'hidden',
                px: 1, // Reduced horizontal padding
                py: 2,
                pt: 1
            }}>
                {/* Header Section */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                        <Typography variant="h4" component="h1" gutterBottom>
                            Data Analysis Chat
                        </Typography>
                        <Typography variant="body1">
                            Ask questions about the BrandSpark ® Trust Survey data. I can help you analyze brands, categories, trust metrics, and consumer insights.
                        </Typography>
                    </Box>
                </Box>

                {/* Main Content Area with Sidebar and Empty State */}
                <Box sx={{ 
                    display: 'flex', 
                    flexGrow: 1,
                    gap: 2,
                    minHeight: 0
                }}>
                    {/* Chat Sidebar */}
                    <ChatSidebar
                        selectedSessionId={sessionId}
                        onSessionSelect={handleSessionSelect}
                        onNewChat={handleNewChat}
                        selectedCountry={selectedCountry}
                        refreshTrigger={sidebarRefreshTrigger}
                        collapsed={sidebarCollapsed}
                        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
                    />

                    {/* Empty State */}
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        flexGrow: 1, 
                        minWidth: 0,
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        <Typography variant="h4" component="h1" gutterBottom color="text.secondary">
                            Welcome to Data Analysis Chat
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, textAlign: 'center', maxWidth: 600 }}>
                            Select an existing chat from the sidebar or click the "+" button to start a new conversation about the BrandSpark ® Trust Survey data.
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={handleNewChat}
                            size="large"
                            sx={{ minWidth: 200 }}
                        >
                            Start New Chat
                        </Button>
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            height: '100%', 
            maxHeight: '100%',
            overflow: 'hidden',
            px: 1, // Reduced horizontal padding
            py: 2,
            pt: 1
        }}>
            {/* Header Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                    <Typography variant="h4" component="h1" gutterBottom>
                        Data Analysis Chat
                    </Typography>
                    <Typography variant="body1">
                        Ask questions about the BrandSpark ® Trust Survey data. I can help you analyze brands, categories, trust metrics, and consumer insights.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Tooltip title="Share chat">
                        <Button
                            variant="outlined"
                            startIcon={<ShareIcon />}
                            onClick={handleShareChat}
                            disabled={!sessionId || isTemporarySession || messages.length <= 1}
                            sx={{
                                whiteSpace: 'nowrap',
                                minWidth: '100px'
                            }}
                        >
                            SHARE
                        </Button>
                    </Tooltip>
                    <Tooltip title="Export chat to PDF">
                        <Button
                            variant="outlined"
                            startIcon={exportingPdf ? <CircularProgress size={16} /> : <DownloadIcon />}
                            onClick={handleExportToPdf}
                            disabled={exportingPdf || messages.length <= 1}
                            sx={{
                                whiteSpace: 'nowrap',
                                minWidth: '120px'
                            }}
                        >
                            {exportingPdf ? 'EXPORTING...' : 'EXPORT PDF'}
                        </Button>
                    </Tooltip>
                </Box>
            </Box>

            {/* Main Content Area with Sidebar and Chat */}
            <Box sx={{ 
                display: 'flex', 
                flexGrow: 1,
                gap: 2,
                minHeight: 0
            }}>
                {/* Chat Sidebar */}
                <ChatSidebar
                    selectedSessionId={sessionId}
                    onSessionSelect={handleSessionSelect}
                    onNewChat={handleNewChat}
                    selectedCountry={selectedCountry}
                    refreshTrigger={sidebarRefreshTrigger}
                    collapsed={sidebarCollapsed}
                    onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
                />

                {/* Chat Messages Area */}
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    flexGrow: 1, 
                    minWidth: 0
                }}>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Chat Messages Container */}
            <Paper
                ref={chatContainerRef}
                elevation={2}
                sx={{
                    p: 2,
                    mb: 2,
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    height: 'calc(100vh - 480px)', // Make chat window shorter
                    minHeight: 'calc(100vh - 480px)', // Ensure consistent height
                    maxHeight: 'calc(100vh - 480px)' // Match sidebar bottom alignment
                }}
            >
                <Box sx={{ flexGrow: 1 }}>
                    {messages.map((message, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                            <Card
                                sx={{
                                    bgcolor: message.role === 'user' ? 'primary.light' : 'grey.100',
                                    color: message.role === 'user' ? 'primary.contrastText' : 'text.primary'
                                }}
                            >
                                <CardContent sx={{ pb: '16px !important' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        {message.role === 'user' ? (
                                            <PersonIcon sx={{ mr: 1, fontSize: 20 }} />
                                        ) : (
                                            <SmartToyIcon sx={{ mr: 1, fontSize: 20 }} />
                                        )}
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                            {message.role === 'user' ? 'You' : 'Assistant'}
                                        </Typography>
                                        <Chip
                                            label={formatTimestamp(message.timestamp)}
                                            size="small"
                                            sx={{ ml: 'auto', fontSize: '0.7rem' }}
                                        />
                                        <Tooltip title="Copy message">
                                            <IconButton
                                                onClick={() => copyToClipboard(message.content)}
                                                size="small"
                                                sx={{ ml: 1 }}
                                            >
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>

                                    {/* Render message content with markdown and charts for assistant messages */}
                                    {message.role === 'assistant' ? (
                                        <Box
                                            sx={{
                                                color: message.isError ? 'error.main' : 'inherit',
                                                '& h1, & h2, & h3, & h4, & h5, & h6': {
                                                    marginTop: 2,
                                                    marginBottom: 1,
                                                    fontWeight: 'bold'
                                                },
                                                '& p': {
                                                    marginBottom: 1
                                                },
                                                '& ul, & ol': {
                                                    marginLeft: 2,
                                                    marginBottom: 1
                                                },
                                                '& li': {
                                                    marginBottom: 0.5
                                                },
                                                '& code': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                                    padding: '2px 4px',
                                                    borderRadius: '4px',
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.9em'
                                                },
                                                '& pre': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                                    padding: 2,
                                                    borderRadius: 1,
                                                    overflow: 'auto',
                                                    marginBottom: 1
                                                },
                                                '& pre code': {
                                                    backgroundColor: 'transparent',
                                                    padding: 0
                                                },
                                                '& blockquote': {
                                                    borderLeft: '4px solid #ccc',
                                                    paddingLeft: 2,
                                                    marginLeft: 0,
                                                    marginBottom: 1,
                                                    fontStyle: 'italic'
                                                },
                                                '& table': {
                                                    borderCollapse: 'collapse',
                                                    width: '100%',
                                                    marginBottom: 1
                                                },
                                                '& th, & td': {
                                                    border: '1px solid #ddd',
                                                    padding: 1,
                                                    textAlign: 'left'
                                                },
                                                '& th': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                                    fontWeight: 'bold'
                                                }
                                            }}
                                        >
                                            {(() => {
                                                // Extract chart data from message content
                                                const charts = extractChartData(message.content);

                                                if (charts.length === 0) {
                                                    // If no charts, just render the markdown
                                                    return <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>;
                                                } else {
                                                    // If charts are found, split the content and render charts in between
                                                    let lastIndex = 0;
                                                    const parts = [];

                                                    charts.forEach((chart, index) => {
                                                        // Add text before the chart
                                                        if (chart.startIndex > lastIndex) {
                                                            const textBefore = message.content.substring(lastIndex, chart.startIndex);
                                                            parts.push(
                                                                <ReactMarkdown key={`text-${index}`} remarkPlugins={[remarkGfm]}>
                                                                    {textBefore}
                                                                </ReactMarkdown>
                                                            );
                                                        }

                                                        // Add the chart
                                                        parts.push(
                                                            <Box key={`chart-${index}`} sx={{ my: 2 }}>
                                                                <Paper elevation={1} sx={{ p: 2 }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                        <BarChartIcon sx={{ mr: 1, color: 'primary.main' }} />
                                                                        <Typography variant="subtitle1" fontWeight="bold">
                                                                            {chart.chartData.title}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Divider sx={{ mb: 2 }} />
                                                                    <ChartRenderer visualization={chart.chartData} />
                                                                </Paper>
                                                            </Box>
                                                        );

                                                        lastIndex = chart.endIndex;
                                                    });

                                                    // Add any remaining text after the last chart
                                                    if (lastIndex < message.content.length) {
                                                        const textAfter = message.content.substring(lastIndex);
                                                        parts.push(
                                                            <ReactMarkdown key="text-end" remarkPlugins={[remarkGfm]}>
                                                                {textAfter}
                                                            </ReactMarkdown>
                                                        );
                                                    }

                                                    return <>{parts}</>;
                                                }
                                            })()}
                                        </Box>
                                    ) : (
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                whiteSpace: 'pre-wrap',
                                                color: message.isError ? 'error.main' : 'inherit'
                                            }}
                                        >
                                            {message.content}
                                        </Typography>
                                    )}

                                    {/* Show tool calls if available */}
                                    {message.toolCalls && message.toolCalls.length > 0 && (
                                        <Box sx={{ mt: 2 }}>
                                            <Accordion>
                                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                    <Typography variant="subtitle2">
                                                        View Tool Calls ({message.toolCalls.length})
                                                    </Typography>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    {message.toolCalls.map((toolCall, tIndex) => (
                                                        <Box key={tIndex} sx={{ mb: 2 }}>
                                                            <Typography variant="subtitle2" gutterBottom>
                                                                Tool: {toolCall.tool}
                                                            </Typography>

                                                            {/* Show tool input */}
                                                            <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                                                Input:
                                                            </Typography>
                                                            <Paper sx={{ p: 1, mb: 1, bgcolor: 'grey.50' }}>
                                                                <Typography variant="body2">
                                                                    <strong>Query:</strong> {toolCall.input.query}
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    <strong>Description:</strong> {toolCall.input.description}
                                                                </Typography>
                                                            </Paper>

                                                            {/* Show tool result */}
                                                            <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                                                Result:
                                                            </Typography>
                                                            <Paper sx={{ p: 1, bgcolor: 'grey.50' }}>
                                                                {toolCall.result.success ? (
                                                                    <Box>
                                                                        <Typography variant="body2" gutterBottom>
                                                                            <strong>Status:</strong> Success
                                                                        </Typography>
                                                                        <Typography variant="body2" gutterBottom>
                                                                            <strong>Rows returned:</strong> {toolCall.result.data?.length || 0}
                                                                        </Typography>
                                                                        {toolCall.result.country && (
                                                                            <Typography variant="body2" gutterBottom>
                                                                                <strong>Country:</strong> {toolCall.result.country}
                                                                            </Typography>
                                                                        )}
                                                                        {toolCall.result.data && toolCall.result.data.length > 0 && (
                                                                            <Box>
                                                                                <Typography variant="body2" gutterBottom>
                                                                                    <strong>Sample data:</strong>
                                                                                </Typography>
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    component="pre"
                                                                                    sx={{
                                                                                        fontFamily: 'monospace',
                                                                                        fontSize: '0.8rem',
                                                                                        maxHeight: '200px',
                                                                                        overflow: 'auto',
                                                                                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                                                                        padding: 1,
                                                                                        borderRadius: 1
                                                                                    }}
                                                                                >
                                                                                    {JSON.stringify(toolCall.result.data.slice(0, 3), null, 2)}
                                                                                    {toolCall.result.data.length > 3 && '\n... and more'}
                                                                                </Typography>
                                                                            </Box>
                                                                        )}
                                                                    </Box>
                                                                ) : (
                                                                    <Box>
                                                                        <Typography variant="body2" color="error" gutterBottom>
                                                                            <strong>Status:</strong> Error
                                                                        </Typography>
                                                                        <Typography variant="body2" color="error">
                                                                            <strong>Error:</strong> {toolCall.result.error}
                                                                        </Typography>
                                                                    </Box>
                                                                )}
                                                            </Paper>
                                                        </Box>
                                                    ))}
                                                </AccordionDetails>
                                            </Accordion>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Box>
                    ))}
                    {loading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                                Assistant is thinking...
                            </Typography>
                        </Box>
                    )}
                </Box>
                <div ref={messagesEndRef} />
            </Paper>

            {/* Message Input */}
            <Paper elevation={2} sx={{ p: 2, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        fullWidth
                        multiline
                        maxRows={4}
                        value={currentMessage}
                        onChange={(e) => setCurrentMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder={'Ask me anything about the trust survey data...'}
                        disabled={loading || !sessionId}
                        variant="outlined"
                        size="small"
                    />
                    <Button
                        variant="contained"
                        onClick={sendMessage}
                        disabled={loading || !currentMessage.trim() || !sessionId}
                        sx={{ minWidth: 'auto', px: 2 }}
                    >
                        {loading ? <CircularProgress size={20} /> : <SendIcon />}
                    </Button>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Press Enter to send, Shift+Enter for new line
                </Typography>
            </Paper>

                    <Snackbar
                        open={copySnackbar}
                        autoHideDuration={3000}
                        onClose={() => setCopySnackbar(false)}
                        message="Copied to clipboard!"
                    />

                    {/* Share Dialog */}
                    <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} maxWidth="md" fullWidth>
                        <DialogTitle>
                            Share Chat
                        </DialogTitle>
                        <DialogContent>
                            <Typography variant="body1" gutterBottom>
                                Share this chat conversation with others using the link below. Anyone with this link can view the chat without needing to log in.
                            </Typography>
                            <Box sx={{ 
                                mt: 2, 
                                p: 2, 
                                bgcolor: '#f5f5f5', 
                                borderRadius: 1,
                                border: '1px solid #ddd',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}>
                                <TextField
                                    fullWidth
                                    value={shareUrl}
                                    variant="outlined"
                                    size="small"
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                />
                                <Tooltip title="Copy link">
                                    <IconButton onClick={handleCopyShareUrl} color="primary">
                                        <ContentCopyIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                Note: This link provides read-only access to the chat conversation. Viewers cannot send new messages or access other features.
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setShareDialogOpen(false)}>
                                Close
                            </Button>
                            <Button onClick={handleCopyShareUrl} variant="contained" startIcon={<ContentCopyIcon />}>
                                Copy Link
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Box>
            </Box>
        </Box>
    );
};

export default Chat;
